import type { ReactElement, ReactNode } from 'react';
import { cloneElement, forwardRef } from 'react';
import { msg } from '@gonfalon/messages/msg';
import { ButtonGroup } from '@launchpad-ui/components';
import type { Icon, IconProps } from '@launchpad-ui/icons';
import cx from 'clsx';

import styles from './ActionBar.module.css';

export type ActionBarKind = 'active' | 'warning' | 'edit' | 'default' | 'ready' | 'readyToStart';

export type ActionBarProps = {
  kind?: ActionBarKind;
  headlineIcon: ReactElement<IconProps, typeof Icon>;
  headline: ReactNode;
  description?: ReactNode;
  footnote?: ReactNode;
  primaryButton?: ReactElement;
  additionalPrimaryButton?: ReactElement;
  secondaryButton?: ReactElement;
};

export const ActionBar = forwardRef<HTMLDivElement, ActionBarProps>(
  (
    {
      kind = 'default',
      headlineIcon,
      headline,
      description,
      footnote,
      primaryButton,
      additionalPrimaryButton,
      secondaryButton,
    },
    ref,
  ) => (
    <div ref={ref} className={cx(styles.container, styles[kind])}>
      <div className={cx(styles.header, { [styles.withFooter]: Boolean(footnote) })}>
        <div className={styles.content}>
          <div className={styles.headline}>
            <span className={styles.headlineIcon}>{cloneElement(headlineIcon, { size: 'small' })}</span>
            {headline}
          </div>
          {description && <div className={styles.description}>{description}</div>}
        </div>
        {(primaryButton || secondaryButton) && (
          <ButtonGroup className={styles.buttonGroup} aria-label={msg('ui.actionBar.groupLabel')}>
            {secondaryButton}
            {additionalPrimaryButton}
            {primaryButton}
          </ButtonGroup>
        )}
      </div>
      {footnote && <div className={styles.footer}>{footnote}</div>}
    </div>
  ),
);
